import { makeStyles, Theme } from "@material-ui/core";
import React, { FC, ReactNode, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router";
import LogoIcon from "../../assets/icons/LogoIcon";
import { getHomePageUrl } from "../../utils/hubRoutes";

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      padding: "24px 16px",
      [theme.breakpoints.up("xs")]: {
        paddingTop: 30,
      },
    },
    logoContainer: {
      paddingBottom: 24,
      textAlign: "center",
      [theme.breakpoints.up("xs")]: {
        paddingBottom: 45,
      },
    },
    content: {
      border: "1px solid rgb(234, 234, 234)",
      boxShadow: "2px 8px 40px 0px rgba(0, 22, 50, 0.06)",
      borderRadius: 16,
      padding: "20px 20px 45px",
      backgroundColor: "white",
      position: "relative",
      width: 570,
      maxWidth: "100%",
      margin: "0 auto",
      [theme.breakpoints.up("xs")]: {
        paddingTop: 30,
      },
    },
    innerContent: {
      margin: "0 auto",
      width: 340,
      maxWidth: "100%",
    },
    crossLink: {
      position: "absolute",
      top: 12,
      right: 12,
      fontSize: 16,
    },
    logo: {
      width: 80,
      height: "auto",
    },
    title: {
      marginBottom: 18,
      fontSize: 20,
      fontWeight: 700,
      fontFamily: "var(--heading-font)",
      textAlign: "center",
      [theme.breakpoints.up("xs")]: {
        marginBottom: 20,
        fontSize: 24,
      },
    },
    subTitle: {
      marginBottom: 12,
      fontSize: 13,
      [theme.breakpoints.up("xs")]: {
        fontSize: 14,
      },
    },
    notice: {
      textAlign: "center",
      padding: "24px 24px 8px",
      lineHeight: 20 / 12,
      fontSize: 12,
      color: "#6c6c6c",
      [theme.breakpoints.up("xs")]: {
        paddingTop: 44,
        lineHeight: 24 / 16,
        fontSize: 16,
      },
      "& a": {
        color: "#333",
        fontFamily: "inherit",
        "&:hover": {
          textDecoration: "none",
        },
      },
    },
  }),
  { name: "Layout" }
);

interface Props {
  title?: string;
  subTitle?: string;
}

export const Layout: FC<Props> = ({ children, title, subTitle }) => {
  const classes = useStyles();
  const { locale } = useParams<{ locale: string }>();
  const { formatMessage } = useIntl();

  const termsLink = formatMessage({ id: "termsLink" }, { domain: process.env.NX_UENI_DOMAIN });
  const privacyLink = formatMessage({ id: "privacyLink" }, { domain: process.env.NX_UENI_DOMAIN });

  const linkTerms = useCallback(
    (chunks: ReactNode) => (
      <a href={termsLink} target="_blank" rel="nofollow noopener noreferrer">
        {chunks}
      </a>
    ),
    [termsLink]
  );

  const linkPrivacy = useCallback(
    (chunks: ReactNode) => (
      <a href={privacyLink} target="_blank" rel="nofollow noopener noreferrer">
        {chunks}
      </a>
    ),
    [privacyLink]
  );

  return (
    <div className={classes.root}>
      <div className={classes.logoContainer}>
        <a href={getHomePageUrl(locale)} rel="noopener noreferrer">
          <LogoIcon viewBox="0 0 80 16" className={classes.logo} />
        </a>
      </div>
      <div className={classes.content}>
        <div className={classes.innerContent}>
          {title && <div className={classes.title}>{title}</div>}
          {subTitle && <div className={classes.subTitle}>{subTitle}</div>}
          {children}
        </div>
      </div>
      <div className={classes.notice}>
        <FormattedMessage id="termsText" values={{ link: linkTerms }} />
        <br />
        <FormattedMessage id="privacyText" values={{ link: linkPrivacy }} />
      </div>
    </div>
  );
};
